import React from 'react'
import ReactDOM from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  children?: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg';
  className?: string
  // New: top-border progress indicator (0-100). When null/undefined, hidden.
  progress?: number | null
  // Height behavior: 'fixed' uses predefined heights, 'auto' fits content
  heightMode?: 'fixed' | 'auto'
}

const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, title, description, children, footer, size = 'md', className, progress, heightMode = 'fixed' }) => {
  React.useEffect(() => {
    if (!open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  React.useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  if (typeof document === 'undefined') return null
  if (!open) return null

  const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'sm:w-[420px] sm:h-[420px]',
    md: 'sm:w-[560px] sm:h-[560px]',
    lg: 'sm:w-[720px] sm:h-[638.5px]',
  }

  const autoSizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'sm:w-[420px] sm:max-h-[80vh]',
    md: 'sm:w-[560px] sm:max-h-[80vh]',
    lg: 'sm:w-[720px] sm:max-h-[80vh]',
  }

  const heightClasses = heightMode === 'auto' ? autoSizeClasses[size] : sizeClasses[size]

  // Clamp progress to [0,100] if provided
  const progressValue = typeof progress === 'number' ? Math.max(0, Math.min(100, progress)) : null

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'dialog-title' : undefined}
        aria-describedby={description ? 'dialog-description' : undefined}
        className={cn(
          // Base panel styling
          'relative bg-white shadow-xl border border-t-0 border-black/10 overflow-hidden animate-in fade-in zoom-in-95 sm:mx-4',
          // Rounded corners on sm+
          'sm:rounded-2xl',
          // Fixed size and layout
          'w-full h-[85vh] sm:h-auto',
          heightClasses,
          'flex flex-col',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top-border progress indicator */}
        {progressValue !== null && (
          <div
            className="absolute inset-x-0 top-0 h-1.5"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressValue)}
          >
            <div className="h-full bg-foreground/80 transition-all duration-300 ease-out" style={{ width: `${progressValue}%` }} />
          </div>
        )}

        {(title || description) && (
          <div className="px-4 sm:px-6 pt-4 sm:pt-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {title && (
                  <h2 id="dialog-title" className="text-lg sm:text-xl font-semibold text-foreground truncate">
                    {title}
                  </h2>
                )}
                {description && (
                  <p id="dialog-description" className="mt-1 text-sm text-foreground/60">
                    {description}
                  </p>
                )}
              </div>
              <button
                className="shrink-0 cursor-pointer rounded-md p-2 text-foreground/60 hover:bg-black/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                aria-label="Close dialog"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
          {children}
        </div>

        {/* Sticky footer */}
        {footer && (
          <div className="border-t border-black/10 bg-white">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default Dialog
